/**
 * 密码强度和熵值计算工具
 */

/**
 * 计算密码的有效长度（考虑重复字符和常见序列）
 * @param password 密码字符串
 * @returns 有效长度
 */
function getEffectiveLength(password: string): number {
  if (!password) return 0;

  // 检查重复字符（如 aaaaa, 111222）
  let effectiveLength = 0;
  let i = 0;
  while (i < password.length) {
    let charCount = 1;
    while (
      i + charCount < password.length &&
      password[i + charCount] === password[i]
    ) {
      charCount++;
    }

    // 重复字符最多计为2个字符的长度
    effectiveLength += Math.min(charCount, 2);
    i += charCount;
  }

  // 检查常见序列
  // 数字序列: 0123456789
  const digitSequence = '0123456789';
  // 字母序列: abcdefghijklmnopqrstuvwxyz, qwertyuiop, asdfghjkl, zxcvbnm
  const lowerSequence = 'abcdefghijklmnopqrstuvwxyz';
  const qwertySequence = 'qwertyuiop';
  const asdfgSequence = 'asdfghjkl';
  const zxcvbSequence = 'zxcvbnm';

  const sequences = [
    digitSequence,
    lowerSequence,
    qwertySequence,
    asdfgSequence,
    zxcvbSequence,
  ];

  // 检查正向和反向序列
  for (const sequence of sequences) {
    // 正向检查
    let maxSeqLen = 0;
    for (let start = 0; start <= sequence.length - 3; start++) {
      for (let len = sequence.length - start; len >= 3; len--) {
        const subseq = sequence.substring(start, start + len);
        if (password.toLowerCase().includes(subseq)) {
          maxSeqLen = Math.max(maxSeqLen, len);
          break;
        }
      }
    }

    // 反向检查
    for (let start = sequence.length - 1; start >= 2; start--) {
      for (let len = start; len >= 3; len--) {
        const subseq = sequence.substring(start - len + 1, start + 1);
        if (password.toLowerCase().includes(subseq)) {
          maxSeqLen = Math.max(maxSeqLen, len);
          break;
        }
      }
    }

    // 如果找到长度>=3的序列，将其长度减至2
    if (maxSeqLen >= 3) {
      effectiveLength = Math.max(1, effectiveLength - maxSeqLen + 2);
    }
  }

  return effectiveLength;
}

/**
 * 计算密码熵值
 * @param password 密码字符串
 * @returns 熵值（bits）
 */
export function calculatePasswordEntropy(password: string): number {
  if (!password || password.length === 0) {
    return 0;
  }

  // 计算字符集大小
  const charsetMap = new Set<string>();

  for (const char of password) {
    if (/[a-z]/.test(char)) {
      charsetMap.add('lower');
    } else if (/[A-Z]/.test(char)) {
      charsetMap.add('upper');
    } else if (/\d/.test(char)) {
      charsetMap.add('digit');
    } else if (/[@$&*]/.test(char)) {
      charsetMap.add('special1');
    } else if (/[-.,]/.test(char)) {
      charsetMap.add('special2');
    } else if (/["#'()+/:;<=>?[\]^{|}~]/.test(char)) {
      charsetMap.add('special3');
    } else {
      // 其他未分类的字符
      charsetMap.add(char);
    }
  }

  // 根据不同字符集计算字符集大小
  let charsetSize = 0;
  if (charsetMap.has('lower')) charsetSize += 26;
  if (charsetMap.has('upper')) charsetSize += 26;
  if (charsetMap.has('digit')) charsetSize += 10;
  if (charsetMap.has('special1')) charsetSize += 5; // @$&*
  if (charsetMap.has('special2')) charsetSize += 5; // _-.,
  if (charsetMap.has('special3')) charsetSize += 22; // "#'()+/:;<=>?[\]^{|}~

  // 对于其他唯一字符，每种加1
  const otherChars =
    charsetMap.size -
    Array.from([
      'lower',
      'upper',
      'digit',
      'special1',
      'special2',
      'special3',
    ]).filter((set) => charsetMap.has(set)).length;
  charsetSize += otherChars;

  // 使用有效长度而不是原始长度
  const effectiveLength = getEffectiveLength(password);

  // 熵值计算公式: log2(字符集大小^有效长度)
  return Math.log2(charsetSize ** effectiveLength);
}

/**
 * 验证密码强度
 * @param password 密码字符串
 * @returns 验证结果对象
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  entropy: number;
  score: number; // 1-5 分，5为最强
  feedback: string[];
} {
  const entropy = calculatePasswordEntropy(password);
  const feedback: string[] = [];

  // 检查字符多样性
  if (password.length >= 8) {
    // 只有当长度足够时才检查其他要求
    if (!/[a-z]/.test(password)) {
      feedback.push('密码应包含小写字母');
    }

    if (!/[A-Z]/.test(password)) {
      feedback.push('密码应包含大写字母');
    }

    if (!/\d/.test(password)) {
      feedback.push('密码应包含数字');
    }

    if (!/[^a-zA-Z0-9]/.test(password)) {
      feedback.push('密码应包含特殊字符');
    }
  }

  // 计算强度分数
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  // 熵值要求
  if (entropy < 60 && password.length >= 8) {
    // 只有当长度足够时才检查熵值
    feedback.push('密码强度较低，建议使用更复杂的组合');
  }

  return {
    isValid: password.length >= 8 && feedback.length === 0 && entropy >= 60,
    entropy,
    score: Math.min(score, 5),
    feedback,
  };
}

/**
 * 密码强度等级
 */
export enum PasswordStrength {
  VeryWeak = 'very-weak',
  Weak = 'weak',
  Medium = 'medium',
  Strong = 'strong',
  VeryStrong = 'very-strong',
}

/**
 * 获取密码强度等级
 * @param password 密码
 * @returns 强度等级
 */
export function getPasswordStrength(password: string): PasswordStrength {
  const entropy = calculatePasswordEntropy(password);

  if (entropy < 35) return PasswordStrength.VeryWeak;
  if (entropy < 50) return PasswordStrength.Weak;
  if (entropy < 65) return PasswordStrength.Medium;
  if (entropy < 80) return PasswordStrength.Strong;
  return PasswordStrength.VeryStrong;
}

/**
 * 生成密码验证规则
 * @param message 自定义错误消息
 * @param mode 验证模式：'strict' | 'simple'
 * - strict: 严格模式（默认），要求 8 位以上，包含大小写字母、数字、特殊字符，熵值>=60
 * - simple: 简单模式，仅要求 4 位以上
 * @returns Ant Design Form 规则
 */
export function getPasswordValidationRules(
  message?: string,
  mode: 'strict' | 'simple' = 'strict',
) {
  if (mode === 'simple') {
    // 简单模式：仅要求长度大于 4 位
    return [
      { required: true, message: message || '请输入密码' },
      { min: 5, message: '密码长度至少为 5 位' },
    ];
  }

  // 严格模式：原有规则
  return [
    { required: true, message: message || '请输入密码' },
    { min: 8, message: '密码长度至少为 8 位' },
    {
      validator: (_: any, value: string) => {
        if (!value) {
          return Promise.resolve();
        }

        const validation = validatePasswordStrength(value);
        if (validation.isValid) {
          return Promise.resolve();
        }

        // 返回第一个反馈作为错误消息
        const firstFeedback = validation.feedback[0];
        return Promise.reject(new Error(firstFeedback || '密码不符合强度要求'));
      },
    },
  ];
}
